import { faExternalLink } from '@fortawesome/pro-regular-svg-icons/faExternalLink';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import { School } from '@propertypal/shared/src/types/schools';
import getAnchor from '@propertypal/shared/src/utils/ui/anchor';
import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { LinkText } from '../typography';
import { InfoClose, InfoPopup, InfoTitle, TableCol, TableRow } from './InfoBox.style';

interface Props {
  school: School;
  onClose: () => void;
  target: HTMLElement;
  offsetTop?: number;
  fixed?: boolean;
}

const InfoBox: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'details' | 'data'>('details');
  const [anchor, setAnchor] = useState('');
  const [style, setStyle] = useState<CSSProperties>();

  const onOutsideClick = (e: Event) => {
    // @ts-ignore
    if (container.current && e.target && !container.current.contains(e.target)) {
      props.onClose();
    }
  };

  useEventListener('click', onOutsideClick);

  useEffect(() => {
    const [mAnchor, mStyle] = getAnchor(props.target, container, props.offsetTop, props.fixed);
    setStyle(mStyle);
    setAnchor(mAnchor);
  }, [props.target, mode]);

  const closeBox = () => {
    setMode('details');
    props.onClose();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeBox();
    }
  };

  useEventListener('keydown', handleKeyDown);

  return (
    <InfoPopup
      className="pp-schools-info-box"
      ref={container}
      anchor={anchor}
      style={{ ...style, width: mode === 'details' ? 250 : 400 }}
      data-testid="info-popup"
    >
      <InfoClose onClick={closeBox} data-testid="closeInfoBox">
        <FontAwesomeIcon color={theme.textDark} icon={faTimes} style={{ fontSize: 14 }} />
      </InfoClose>

      <div style={{ display: mode === 'details' ? 'block' : 'none' }}>
        <InfoTitle className="pp-schools-info-box-title">{props.school.name}</InfoTitle>

        <p>{props.school.fullAddress}</p>
        <p>
          <b>{props.school.phoneNumber}</b>
        </p>

        {props.school.websiteUrl && (
          <p>
            <LinkText href={props.school.websiteUrl} target="_blank" rel="nofollow">
              View website <FontAwesomeIcon icon={faExternalLink} size={10} />
            </LinkText>{' '}
          </p>
        )}

        <p>
          <LinkText as="span" onClick={() => setMode('data')} data-testid="viewPreviousData">
            View previous data <Chevron size={10} />
          </LinkText>
        </p>
      </div>

      {!props.school.roi && (
        <TableRow style={{ display: mode === 'data' ? 'flex' : 'none' }}>
          <TableCol>
            <h4>Total applications for previous school years</h4>
            <ul>
              {props.school.years.map((year) => (
                <li key={year.year}>
                  <b>{year.year}</b> {year.totalApplications}
                </li>
              ))}
            </ul>
          </TableCol>

          <TableCol>
            <h4>Total admissions for previous school years</h4>
            <ul>
              {props.school.years.map((year) => (
                <li key={year.year}>
                  <b>{year.year}</b> {year.totalAdmissions}
                </li>
              ))}
            </ul>
          </TableCol>
        </TableRow>
      )}

      {props.school.roi && (
        <TableCol style={{ display: mode === 'data' ? 'flex' : 'none' }}>
          <h4>Total pupils for previous school years</h4>

          <ul>
            {props.school.years.map((year) => (
              <li key={year.year}>
                <b>{year.year}</b> {year.totalPupilsROI}
              </li>
            ))}
          </ul>
        </TableCol>
      )}
    </InfoPopup>
  );
};

export default InfoBox;
